import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6 gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            トップへ戻る
          </Button>
        </Link>

        <h1 className="text-lg font-bold mb-1">プライバシーポリシー</h1>
        <p className="text-xs text-muted-foreground mb-6">最終更新日: 2026年2月18日</p>

        <div className="max-w-none space-y-4 text-xs">
          <section>
            <h2 className="text-sm font-semibold mb-1">1. はじめに</h2>
            <p className="text-muted-foreground leading-relaxed">
              Hitorigoto（以下「本サービス」といいます）は、ユーザーのプライバシーの保護を重要な責務と認識し、個人情報の保護に関する法律（以下「個人情報保護法」といいます）その他の関連法令およびガイドラインを遵守するとともに、本プライバシーポリシー（以下「本ポリシー」といいます）に従い、適切に個人情報の取扱いおよび保護を行います。
              本ポリシーは、本サービスが収集する情報の種類、その収集方法、利用目的、第三者への提供、保管方法、ならびにユーザーの権利および選択肢について包括的に説明するものです。
              本サービスを利用することにより、ユーザーは本ポリシーに記載された情報の取扱いに同意したものとみなされます。本ポリシーに同意いただけない場合は、本サービスのご利用をお控えください。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">2. 定義</h2>
            <p className="text-muted-foreground leading-relaxed mb-1">本ポリシーにおいて、以下の用語は次の意味を有するものとします。</p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>「個人情報」とは、生存する個人に関する情報であって、当該情報に含まれる氏名、メールアドレスその他の記述等により特定の個人を識別することができるもの、および個人識別符号が含まれるものをいいます。</li>
              <li>「ユーザー」とは、本サービスを利用するすべての個人をいいます。ゲストアカウントを通じて本サービスを利用する方を含みます。</li>
              <li>「コンテンツデータ」とは、ユーザーが本サービスを通じて作成、記録、保存した音声データ、テキストデータ、およびそれらから生成された要約・分析結果等の一切のデータをいいます。</li>
              <li>「利用データ」とは、ユーザーが本サービスを利用する過程で自動的に収集される、アクセス日時、利用した機能、ページ遷移、デバイス情報、ブラウザの種類、言語設定、画面解像度、OS情報等の情報をいいます。</li>
            </ul>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">3. 収集する情報</h2>
            <p className="text-muted-foreground leading-relaxed mb-1">本サービスでは、サービスの提供および改善を目的として、以下の情報を収集することがあります。</p>
            <h3 className="text-xs font-semibold mt-2 mb-1 text-muted-foreground">3.1 ユーザーが直接提供する情報</h3>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>アカウント登録時に提供されるメールアドレスおよびパスワード</li>
              <li>プロフィール情報として設定されるニックネーム</li>
              <li>録音された音声データ</li>
              <li>音声から文字起こしされたテキスト、ユーザーが編集・追記したテキスト</li>
              <li>サポートへの問い合わせ内容</li>
            </ul>
            <h3 className="text-xs font-semibold mt-2 mb-1 text-muted-foreground">3.2 自動的に収集される情報</h3>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>アクセスログ（アクセス日時、IPアドレス、リファラー情報）</li>
              <li>デバイスおよびブラウザに関する情報（デバイスの種類、OS、ブラウザの種類およびバージョン、画面解像度、言語設定）</li>
              <li>サービスの利用状況に関する情報（閲覧ページ、機能の利用頻度、セッション時間、操作ログ）</li>
              <li>Cookie およびこれに類似する技術を通じて取得される情報</li>
            </ul>
            <h3 className="text-xs font-semibold mt-2 mb-1 text-muted-foreground">3.3 第三者から取得する情報</h3>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>OAuth認証を利用した場合の認証プロバイダーから提供される情報（メールアドレス、表示名、プロフィール画像URL等）</li>
              <li>決済代行サービスから提供される決済ステータスに関する情報（カード番号等の機密情報は含まれません）</li>
            </ul>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">4. 情報の利用目的</h2>
            <p className="text-muted-foreground leading-relaxed mb-1">収集した情報は、以下の目的のために利用します。</p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>本サービスの基本機能（日記の作成、保存、表示、検索、振り返り機能等）の提供</li>
              <li>音声データの文字起こし処理の実行</li>
              <li>AI による日記テキストの要約、感情分析、カテゴリ分類、および振り返りコンテンツの自動生成</li>
              <li>ユーザーのアカウント管理、認証および本人確認</li>
              <li>有料プランに関する決済処理およびサブスクリプション管理</li>
              <li>ユーザーからの問い合わせ、サポートリクエストへの対応</li>
              <li>本サービスの利用状況の分析、品質向上、新機能開発のための統計処理</li>
              <li>本サービスに関する重要な通知（メンテナンス、規約変更等）の送信</li>
              <li>不正行為の検出、セキュリティインシデントの予防および対応</li>
              <li>法令上の義務の履行、権利の行使および防御</li>
            </ul>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">5. 外部サービスとの連携</h2>
            <p className="text-muted-foreground leading-relaxed mb-1">
              本サービスでは、機能の提供・運営を目的として、以下の種類の外部サービスを利用しています。これらのサービスへのデータの送信は、サービス提供に必要な最小限の範囲で行われます。各外部サービスにおけるデータの取扱いについては、当該サービスのプライバシーポリシーが適用されます。
            </p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li><strong>クラウドデータベース・認証サービス</strong> — アカウント情報、認証トークン、および日記データの保管・管理に利用します。データは暗号化された通信経路を介して送受信されます。</li>
              <li><strong>音声認識サービス</strong> — 音声データからテキストへの変換処理に利用します。音声データは文字起こし処理のために外部サーバーに送信されますが、処理完了後に当該サーバー上で永続的に保存されることはありません。</li>
              <li><strong>AI 分析サービス</strong> — 日記テキストの要約生成、感情分析、振り返りコンテンツの生成等に利用します。処理のために日記テキストが外部サーバーに送信されます。送信されたデータがAIモデルの学習に使用されることはありません。</li>
              <li><strong>決済代行サービス</strong> — 有料プランの決済処理、サブスクリプション管理に利用します。クレジットカード情報等の機密性の高い決済情報は決済代行サービスが PCI DSS に準拠した環境で安全に管理し、本サービスのサーバーでは一切保持しません。</li>
              <li><strong>アクセス解析サービス（Google Analytics）</strong> — サービスの利用状況の把握および改善のため、ページの閲覧状況、ユーザーの利用傾向、流入経路等を匿名化された形で収集・分析します。Cookie やこれに類似する技術を使用する場合があります。</li>
            </ul>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">6. Cookie およびトラッキング技術</h2>
            <p className="text-muted-foreground leading-relaxed">
              本サービスでは、ユーザー体験の向上およびサービス改善を目的として、Cookie およびローカルストレージ等のブラウザストレージ技術を使用しています。
              Cookie とは、ウェブサイトがユーザーのブラウザに送信し、ユーザーのデバイスに保存される小さなテキストファイルです。
              本サービスでは、セッション管理、ユーザー設定の保持（テーマ、フォントサイズ等）、およびアクセス解析を目的として Cookie を使用します。
              ユーザーは、ブラウザの設定により Cookie の受け入れを拒否することができますが、その場合、本サービスの一部機能が正常に動作しない可能性があります。
              なお、アクセス解析に使用される Cookie は、個人を直接特定する情報を含みません。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">7. データの保管とセキュリティ</h2>
            <p className="text-muted-foreground leading-relaxed mb-1">
              本サービスは、ユーザーの個人情報およびコンテンツデータを適切に保護するため、以下のセキュリティ対策を講じています。
            </p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>すべての通信における SSL/TLS 暗号化の適用</li>
              <li>パスワードのハッシュ化処理による保管</li>
              <li>データベースへのアクセス制御の実施</li>
              <li>クラウドインフラストラクチャの提供するセキュリティ機能の活用</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-1">
              ただし、本サービスは個人開発のプロダクトであり、運営者がサービスの運用・保守を目的としてデータベースに直接アクセスできる状態にあります。
              運営者は、ユーザーのデータを業務上必要な範囲（不具合対応、サポート対応、サービスの安定運用等）でのみ閲覧し、これを不正に利用し、または業務上必要な範囲を超えて第三者に開示することはありません。
              また、インターネット上のデータ送信は完全な安全性を保証するものではなく、送信中のデータの傍受や不正アクセスのリスクを完全に排除することはできません。運営者はかかるリスクについて責任を負いかねます。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">8. データの保存期間</h2>
            <p className="text-muted-foreground leading-relaxed">
              ユーザーの個人情報およびコンテンツデータは、ユーザーがアカウントを保有している期間中、本サービスのサーバー上に保管されます。
              ユーザーがアカウントを削除した場合、当該ユーザーに関連する個人情報およびコンテンツデータは、合理的な期間内に削除されます。ただし、法令上保存が義務付けられている情報、不正行為の調査に必要な情報、または技術的な理由（バックアップからの削除を含む）により即座に削除できない情報については、必要な期間に限り保持される場合があります。
              ゲストアカウントに関連するデータは、一定期間の非活動状態が続いた場合に、事前通知なく削除されることがあります。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">9. 第三者提供</h2>
            <p className="text-muted-foreground leading-relaxed">
              運営者は、以下の場合を除き、ユーザーの同意を得ることなく個人情報を第三者に提供することはありません。
            </p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>法令に基づく場合（裁判所の命令、捜査機関からの照会等）</li>
              <li>人の生命、身体または財産の保護のために必要がある場合であって、ユーザーの同意を得ることが困難であるとき</li>
              <li>公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、ユーザーの同意を得ることが困難であるとき</li>
              <li>国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、ユーザーの同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-1">
              なお、前条に定める外部サービスへのデータ送信は、サービス提供に不可欠な業務委託として行われるものであり、ここでいう第三者提供には該当しません。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">10. ユーザーの権利</h2>
            <p className="text-muted-foreground leading-relaxed mb-1">ユーザーは、自身の個人情報に関して以下の権利を有します。運営者は、ユーザーからの請求に対し、合理的な期間内に対応します。</p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li><strong>アクセス権</strong> — 自身の個人情報がどのように収集・利用されているかについて確認を求める権利</li>
              <li><strong>訂正権</strong> — 不正確または不完全な個人情報の訂正・更新を求める権利</li>
              <li><strong>削除権</strong> — 自身の個人情報およびアカウントの削除を求める権利（設定画面またはサポートへのご連絡により対応いたします）</li>
              <li><strong>データポータビリティ</strong> — 自身のコンテンツデータを一般的に利用可能な形式で取得する権利</li>
              <li><strong>異議申立権</strong> — 個人情報の特定の利用方法について異議を申し立てる権利</li>
            </ul>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">11. 未成年者の利用</h2>
            <p className="text-muted-foreground leading-relaxed">
              本サービスは、原則として16歳以上の方を対象としています。16歳未満の方が本サービスを利用する場合は、保護者の同意を得た上でご利用ください。
              運営者が16歳未満の方の個人情報を保護者の同意なく収集していたことが判明した場合、速やかに当該情報を削除する措置を講じます。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">12. 国際的なデータ移転</h2>
            <p className="text-muted-foreground leading-relaxed">
              本サービスが利用する外部サービスのサーバーは、日本国外に所在する場合があります。そのため、ユーザーの個人情報およびコンテンツデータは、日本国外のサーバーに転送・保管される可能性があります。
              かかるデータの移転は、サービス提供に必要な範囲で行われ、各外部サービスが提供するセキュリティ基準に基づいて保護されます。
              本サービスを利用することにより、ユーザーはかかる国際的なデータ移転に同意したものとみなされます。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">13. ポリシーの変更</h2>
            <p className="text-muted-foreground leading-relaxed">
              運営者は、法令の改正、サービス内容の変更、その他の事由により、本ポリシーを変更する必要が生じた場合、本ポリシーを随時改定することができるものとします。
              重要な変更がある場合は、本サービス内での通知、メールの送信、またはウェブサイト上への掲載等の適切な方法により、変更内容をユーザーに通知します。
              変更後の本ポリシーは、本サービス上に掲載された時点から効力を生じるものとし、変更後も本サービスを継続して利用する場合、ユーザーは変更後の本ポリシーに同意したものとみなされます。
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-1">14. お問い合わせ</h2>
            <p className="text-muted-foreground leading-relaxed">
              本ポリシーに関するご質問、ご意見、個人情報の取扱いに関する苦情およびご相談は、アプリ内のサポート機能よりご連絡ください。
              運営者は、受領したお問い合わせに対し、合理的な期間内に回答するよう努めます。
            </p>
          </section>
        </div>

        <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
          <Link to="/terms" className="hover:underline">利用規約</Link>
          <span className="mx-2">|</span>
          <Link to="/" className="hover:underline">トップページ</Link>
        </div>
      </div>
    </div>
  );
}
